import { z } from "zod";
import xss from "xss";
import tlds from "tlds";

export const createAmountSchema = (t, min, max, minMessage, maxMessage) =>
  z.object({
    amount: z
      .number({
        required_error: t("onboarding.onboardingInvalidMessages.amountRequired"),
        invalid_type_error: t("onboarding.onboardingInvalidMessages.amountInvalid")
      })
      .min(min, {
        message: minMessage
      })
      .max(max, {
        message: maxMessage
      })
  });

export const createOrgNumberSchema = (t) =>
  z.object({
    business_id: z.string().regex(/^\d{6}-\d{4}$/, {
      message: t("onboarding.onboardingInvalidMessages.businessIdInvalid")
    })
  });

export const createPersonNumberSchema = (t) =>
  z.object({
    civic_number: z
      .string()
      .max(13, {
        message: t("onboarding.onboardingInvalidMessages.civicNumberMax")
      })
      .regex(/^\d{8}-\d{4}$/, {
        message: t("onboarding.onboardingInvalidMessages.civicNumberInvalid")
      })
      .transform((val) => val.trim())
      .optional()
      .or(z.literal(""))
  });

export const createContactSchema = (t) =>
  z.object({
    email: z
      .string()
      .min(1, {
        message: t("onboarding.onboardingInvalidMessages.emailRequired")
      })
      .email({
        message: t("onboarding.onboardingInvalidMessages.emailInvalid")
      })
      .refine(
        (email) => {
          const [, domain] = email.split("@");
          if (!domain) return false;

          const tld = domain.split(".").pop()?.toLowerCase();
          return !!tld && tlds.includes(tld);
        },
        {
          message: t("onboarding.onboardingInvalidMessages.emailInvalid")
        }
      ),

    mobilephone: z.string().regex(/^\+46[1-9]\d{7,8}$/, {
      message: t("onboarding.onboardingInvalidMessages.phoneInvalid")
    })
  });

export const createLoanDescriptionSchema = (t) =>
  z.object({
    purpose_freetext: z
      .string()
      .superRefine((val, ctx) => {
        const trimmed = val.trim();

        if (trimmed.length < 5) {
          ctx.addIssue({
            code: "too_small",
            minimum: 5,
            type: "string",
            inclusive: true,
            message: t("onboarding.onboardingInvalidMessages.purposeTooShort")
          });
        }

        if (trimmed.length > 500) {
          ctx.addIssue({
            code: "too_big",
            maximum: 500,
            type: "string",
            inclusive: true,
            message: t("onboarding.onboardingInvalidMessages.purposeTooLong")
          });
        }

        if (/<script.*?>.*?<\/script>/gi.test(trimmed)) {
          ctx.addIssue({
            code: "custom",
            message: t("onboarding.onboardingInvalidMessages.scriptNotAllowed")
          });
        }

        const allowedBoundaryChars = /^[a-zA-Z0-9åäöÅÄÖ]/;
        const allowedEndChars = /[a-zA-Z0-9åäöÅÄÖ.!?]$/;

        if (!allowedBoundaryChars.test(trimmed)) {
          ctx.addIssue({
            code: "custom",
            message: t("onboarding.onboardingInvalidMessages.startsWithSpecialChar")
          });
        }

        if (!allowedEndChars.test(trimmed)) {
          ctx.addIssue({
            code: "custom",
            message: t("onboarding.onboardingInvalidMessages.endsWithSpecialChar")
          });
        }
      })
      .transform((val) => xss(val.trim()))
  });

export const createContactFormSchema = (t) =>
  z.object({
    firstname: z
      .string()
      .min(2, {
        message: "Förnamnet måste innehålla minst 2 tecken"
      })
      .max(50, {
        message: "Förnamnet får inte vara längre än 50 tecken"
      })
      .regex(/^[a-zA-ZåäöÅÄÖ\s'-]+$/, {
        message: "Förnamnet innehåller ogiltiga tecken"
      }),

    lastname: z
      .string()
      .min(2, {
        message: "Efternamnet måste innehålla minst 2 tecken"
      })
      .max(50, {
        message: "Efternamnet får inte vara längre än 50 tecken"
      })
      .regex(/^[a-zA-ZåäöÅÄÖ\s'-]+$/, {
        message: "Efternamnet innehåller ogiltiga tecken"
      }),

    email: z
      .string()
      .min(1, {
        message: t("onboarding.onboardingInvalidMessages.emailRequired")
      })
      .email({
        message: t("onboarding.onboardingInvalidMessages.emailInvalid")
      })
      .refine(
        (email) => {
          const [, domain] = email.split("@");
          if (!domain) return false;

          const tld = domain.split(".").pop()?.toLowerCase();
          return !!tld && tlds.includes(tld);
        },
        {
          message: t("onboarding.onboardingInvalidMessages.emailInvalid")
        }
      ),

    mobilephone: z.string().regex(/^\+46[1-9]\d{7,8}$/, {
      message: t("onboarding.onboardingInvalidMessages.phoneInvalid")
    }),

    business_id: z
      .string()
      .regex(/^\d{6}-\d{4}$/, {
        message: t("onboarding.onboardingInvalidMessages.businessIdInvalid")
      })
      .optional()
      .or(z.literal("")),
    content: z
      .string()
      .optional()
      .or(z.literal(""))
      .superRefine((val, ctx) => {
        if (!val || val.trim() === "") {
          return;
        }
        const trimmed = val.trim();

        if (trimmed.length < 5) {
          ctx.addIssue({
            code: "too_small",
            minimum: 5,
            type: "string",
            inclusive: true,
            message: t("onboarding.onboardingInvalidMessages.purposeTooShort")
          });
        }

        if (trimmed.length > 500) {
          ctx.addIssue({
            code: "too_big",
            maximum: 500,
            type: "string",
            inclusive: true,
            message: t("onboarding.onboardingInvalidMessages.purposeTooLong")
          });
        }

        if (/<script.*?>.*?<\/script>/gi.test(trimmed)) {
          ctx.addIssue({
            code: "custom",
            message: t("onboarding.onboardingInvalidMessages.scriptNotAllowed")
          });
        }

        const allowedBoundaryChars = /^[a-zA-Z0-9åäöÅÄÖ]/;
        const allowedEndChars = /[a-zA-Z0-9åäöÅÄÖ.!?]$/;

        if (!allowedBoundaryChars.test(trimmed)) {
          ctx.addIssue({
            code: "custom",
            message: t("onboarding.onboardingInvalidMessages.startsWithSpecialChar")
          });
        }

        if (!allowedEndChars.test(trimmed)) {
          ctx.addIssue({
            code: "custom",
            message: t("onboarding.onboardingInvalidMessages.endsWithSpecialChar")
          });
        }
      })
      .transform((val) => xss(val.trim()))
  });

export default {
  createAmountSchema,
  createOrgNumberSchema,
  createPersonNumberSchema,
  createContactSchema,
  createLoanDescriptionSchema,
  createContactFormSchema
};
